import { TOKEN_KEY } from "@selector/shared/settings";
import { type FC, type PropsWithChildren, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { LoadingSpinner } from "widgets";
import { storage } from "widgets";
import { useURLSearchParam } from "widgets/hooks";

import type { User } from "@/graphql.ts";
import { redirectTo, setAuthCookie } from "@/redux/modules/auth.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";

export const RequireAuthentication: FC<PropsWithChildren> = ({ children }) => {
  const isAuthenticated = useSelector<IAppState, boolean>((state) => {
    return state.auth.isAuthenticated && !state.auth.isAuthenticating;
  });
  const session = useSelector<IAppState, User>((state) => state.auth.session);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<ReduxDispatch>();

  let redirect = useURLSearchParam("redirect");
  if (redirect) {
    redirect = decodeURIComponent(redirect);
  }

  useEffect(() => {
    if (isAuthenticated) {
      // Whatever the page, if it has a redirect in url, we redirect to it.
      if (redirect) {
        // in case the Classic UI requires the cookie to be set
        setAuthCookie(storage[TOKEN_KEY]);
        // Classic UI redirect
        dispatch(redirectTo(redirect));
        return;
      }
    } else {
      /**
       * If not logged in, we keep the current url as search param to redirect to.
       * @see AuthLoginPage
       */
      const path = encodeURIComponent(redirect ?? location.pathname + location.search);
      navigate(`/login?redirect=${path}`);
      return;
    }
  }, [isAuthenticated, redirect]);

  if (isAuthenticated && !redirect) {
    return session ? <>{children}</> : <LoadingSpinner inCenter />;
  }

  return null;
};
